// FILE: src/components/ReportedQuestionsView.js
//
// Admin view with two tabs:
//
//   "Reports" tab (existing):
//     Triage question reports submitted by learners and SMEs.
//
//   "Question Bank" tab (new):
//     Review question lifecycle — filter by status/reviewStatus/exam/category,
//     approve new questions, update review status, open in editor.
//
// ── Props ─────────────────────────────────────────────────────────────────────
//
//   reports              {object[]} — full report array from App.js state
//   onUpdateReport       {function} — (reportId, patch) → void
//   onEditQuestion       {function} — (examId, questionId) → void
//   onRemoveReport       {function} — (reportId) → void
//   onBack               {function} — returns to the Admin hub
//   questionStores       {object}   — { pmp: Question[]|null, cpmai: Question[]|null }
//   onUpdateLifecycle    {function} — (questionId, examId, { status?, reviewStatus? }, changeNote) → void

import React, { useState, useMemo, useEffect } from 'react';
import { REPORT_CATEGORIES, REPORT_STATUSES } from '../../../services/reportService';
import {
  QUESTION_STATUSES, QUESTION_STATUS_LABELS, QUESTION_STATUS_COLORS,
  REVIEW_STATUSES, REVIEW_STATUS_LABELS, REVIEW_STATUS_COLORS,
} from '../../../config/lifecycleConstants';
import { getAllExams, getActiveCertIds } from '../../../config/exams';
import { loadAllCertBanks } from '../../../services/certBankLoader';

// ── Helpers ───────────────────────────────────────────────────────────────────

const REPORT_STATUS_COLORS = {
  'new':        { color: '#c62828', bg: '#ffebee' },
  'fixed':      { color: '#2e7d32', bg: '#f1f8f1' },
  "won't fix":  { color: '#777',    bg: '#f5f5f5' },
};

function formatDate(ts) {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDateShort(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  wrapper: {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    minHeight: '100vh',
    background: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: '#1a1a2e',
    color: '#fff',
    padding: '16px 28px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
  },
  backButton: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: '0.85rem',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: '1.15rem',
    fontWeight: 'bold',
    flex: 1,
    letterSpacing: '0.5px',
  },
  headerCount: {
    fontSize: '0.8rem',
    color: '#aaa',
  },
  // Tab bar
  tabBar: {
    display: 'flex',
    background: '#fff',
    borderBottom: '2px solid #e0e0e0',
    padding: '0 28px',
    flexShrink: 0,
  },
  tab: (active) => ({
    padding: '12px 20px',
    fontSize: '0.9rem',
    fontWeight: active ? '700' : '500',
    color: active ? '#1a1a2e' : '#888',
    borderBottom: active ? '2px solid #1a1a2e' : '2px solid transparent',
    cursor: 'pointer',
    marginBottom: '-2px',
    transition: 'color 0.15s',
    userSelect: 'none',
  }),
  body: {
    flex: 1,
    padding: '28px 32px',
    maxWidth: '1000px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  filterBar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    minWidth: '140px',
  },
  filterLabel: {
    fontSize: '0.75rem',
    color: '#888',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  filterSelect: {
    padding: '7px 10px',
    fontSize: '0.9rem',
    border: '1px solid #ccc',
    borderRadius: '6px',
    background: '#fff',
    color: '#333',
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    color: '#aaa',
    padding: '48px 0',
    fontStyle: 'italic',
    fontSize: '0.95rem',
  },
  reportList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  reportCard: (isExpanded) => ({
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    boxShadow: isExpanded ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
    transition: 'box-shadow 0.15s',
  }),
  reportRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    flexWrap: 'wrap',
  },
  refIdBadge: {
    fontFamily: "'Consolas', 'Monaco', monospace",
    fontSize: '0.72rem',
    color: '#666',
    background: '#f0f0f0',
    border: '1px solid #ddd',
    borderRadius: '3px',
    padding: '2px 7px',
    flexShrink: 0,
    letterSpacing: '0.3px',
  },
  examChip: (examId) => ({
    fontSize: '0.7rem',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    padding: '2px 8px',
    borderRadius: '12px',
    flexShrink: 0,
    background: examId === 'pmp' ? '#1a1a2e' : '#1b5e20',
    color: '#fff',
  }),
  categoryTag: {
    fontSize: '0.78rem',
    color: '#555',
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: '2px 8px',
    flexShrink: 0,
  },
  statusBadge: (status) => {
    const cfg = REPORT_STATUS_COLORS[status] || REPORT_STATUS_COLORS['new'];
    return {
      fontSize: '0.73rem',
      fontWeight: '700',
      color: cfg.color,
      background: cfg.bg,
      border: `1px solid ${cfg.color}`,
      borderRadius: '12px',
      padding: '2px 9px',
      flexShrink: 0,
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
    };
  },
  questionPreview: {
    fontSize: '0.88rem',
    color: '#444',
    flex: 1,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    minWidth: 0,
  },
  chevron: {
    fontSize: '0.7rem',
    color: '#bbb',
    flexShrink: 0,
  },
  expandedDetail: {
    padding: '16px 16px 20px 16px',
    borderTop: '1px solid #f0f0f0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  detailRow: {
    display: 'flex',
    gap: '10px',
    fontSize: '0.88rem',
  },
  detailLabel: {
    color: '#888',
    fontWeight: '600',
    minWidth: '130px',
    flexShrink: 0,
    fontSize: '0.82rem',
  },
  detailValue: {
    color: '#333',
    lineHeight: '1.5',
    flex: 1,
  },
  statusSelectRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    paddingTop: '4px',
    borderTop: '1px solid #f5f5f5',
  },
  statusSelectLabel: {
    fontSize: '0.82rem',
    fontWeight: '600',
    color: '#666',
  },
  statusSelect: {
    padding: '6px 10px',
    fontSize: '0.88rem',
    border: '1px solid #ccc',
    borderRadius: '6px',
    background: '#fff',
    color: '#333',
    cursor: 'pointer',
  },
  editQuestionButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: '#1a1a2e',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.88rem',
    fontWeight: '600',
    letterSpacing: '0.3px',
    alignSelf: 'flex-start',
  },
  resolvedBanner: (isFixed) => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 14px',
    borderRadius: '6px',
    background: isFixed ? '#f1f8f1' : '#f5f5f5',
    border: `1px solid ${isFixed ? '#a5d6a7' : '#ddd'}`,
  }),
  resolvedBannerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  resolvedBannerHeading: (isFixed) => ({
    fontSize: '0.85rem',
    fontWeight: '700',
    color: isFixed ? '#2e7d32' : '#666',
  }),
  resolvedBannerBody: {
    fontSize: '0.81rem',
    color: '#888',
    lineHeight: '1.4',
  },
  removeButton: {
    flexShrink: 0,
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #a5d6a7',
    borderRadius: '6px',
    color: '#2e7d32',
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  notesRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  notesLabel: {
    fontSize: '0.82rem',
    fontWeight: '600',
    color: '#666',
  },
  notesTextarea: {
    padding: '8px 10px',
    fontSize: '0.87rem',
    border: '1px solid #ccc',
    borderRadius: '6px',
    resize: 'vertical',
    minHeight: '64px',
    fontFamily: 'inherit',
    lineHeight: '1.5',
    color: '#333',
    width: '100%',
    boxSizing: 'border-box',
  },

  // ── Quick-filter chips ───────────────────────────────────────────────────
  quickFilterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '12px',
  },
  quickFilterChip: (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.82rem',
    fontWeight: '600',
    cursor: 'pointer',
    border: active ? '1.5px solid #1565c0' : '1.5px solid #ccc',
    background: active ? '#e3f2fd' : '#fff',
    color: active ? '#1565c0' : '#555',
    transition: 'all 0.15s',
  }),
  quickFilterBadge: (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '18px',
    padding: '0 5px',
    borderRadius: '9px',
    fontSize: '0.75rem',
    fontWeight: '700',
    background: active ? '#1565c0' : '#e0e0e0',
    color: active ? '#fff' : '#555',
  }),

  // ── Question Bank tab styles ──────────────────────────────────────────────
  qbTable: {
    width: '100%',
    borderCollapse: 'collapse',
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
    fontSize: '0.87rem',
    tableLayout: 'fixed',
  },
  qbTh: {
    padding: '10px 14px',
    background: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
    textAlign: 'left',
    fontWeight: '700',
    color: '#555',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  qbTd: {
    padding: '10px 14px',
    borderBottom: '1px solid #f0f0f0',
    verticalAlign: 'middle',
    overflow: 'hidden',
  },
  lifecycleBadge: (cfg) => ({
    fontSize: '0.7rem',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '10px',
    background: cfg.bg,
    color: cfg.color,
    border: `1px solid ${cfg.border}`,
    whiteSpace: 'nowrap',
    display: 'inline-block',
  }),
  actionButton: (variant) => {
    const map = {
      approve:  { bg: '#2e7d32', color: '#fff', border: 'none' },
      report:   { bg: 'transparent', color: '#c62828', border: '1px solid #ef9a9a' },
      fix:      { bg: 'transparent', color: '#2e7d32', border: '1px solid #a5d6a7' },
      notFixed: { bg: 'transparent', color: '#777',    border: '1px solid #bdbdbd' },
      edit:     { bg: '#1a1a2e',     color: '#fff',    border: 'none' },
    };
    const v = map[variant] || map.edit;
    return {
      padding: '4px 10px',
      fontSize: '0.75rem',
      fontWeight: '600',
      background: v.bg,
      color: v.color,
      border: v.border,
      borderRadius: '5px',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    };
  },
};

// ── ReportCard sub-component ──────────────────────────────────────────────────

function ReportCard({ report, isExpanded, onToggle, onUpdateReport, onRemoveReport, onEditQuestion }) {
  const {
    reportId, questionId, referenceId, examId, category, status,
    questionSnapshot, comments, notes = '', selectedAnswers, reporter, createdAt,
  } = report;

  const [notesDraft, setNotesDraft] = React.useState(notes);
  React.useEffect(() => { setNotesDraft(notes); }, [notes]);

  function handleStatusChange(e) {
    e.stopPropagation();
    onUpdateReport(reportId, { status: e.target.value });
  }

  function handleNotesBlur() {
    if (notesDraft.trim() !== notes.trim()) {
      onUpdateReport(reportId, { notes: notesDraft.trim() });
    }
  }

  return (
    <div
      data-testid="report-item"
      data-report-id={reportId}
      data-status={status}
      style={styles.reportCard(isExpanded)}
      onClick={onToggle}
    >
      <div style={styles.reportRow}>
        {referenceId && <span style={styles.refIdBadge}>{referenceId}</span>}
        <span style={styles.examChip(examId)}>{examId}</span>
        <span style={styles.categoryTag}>{category}</span>
        <span style={styles.statusBadge(status)}>{status}</span>
        <span style={styles.questionPreview}>{questionSnapshot}</span>
        <span style={styles.chevron}>{isExpanded ? '▲' : '▼'}</span>
      </div>

      {isExpanded && (
        <div style={styles.expandedDetail} onClick={(e) => e.stopPropagation()}>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Question:</span>
            <span style={styles.detailValue}>{questionSnapshot}</span>
          </div>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Comments:</span>
            <span style={styles.detailValue}>{comments}</span>
          </div>
          {selectedAnswers.length > 0 && (
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Learner's answers:</span>
              <span style={styles.detailValue}>{selectedAnswers.join(', ')}</span>
            </div>
          )}
          {reporter && (
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Reporter:</span>
              <span style={styles.detailValue}>{reporter}</span>
            </div>
          )}
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Filed:</span>
            <span style={styles.detailValue}>{formatDate(createdAt)}</span>
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="edit-question-button"
              style={styles.editQuestionButton}
              onClick={() => onEditQuestion(examId, questionId)}
            >
              ✎ Edit Question
            </button>
          </div>
          <div style={styles.notesRow} onClick={(e) => e.stopPropagation()}>
            <span style={styles.notesLabel}>SME notes</span>
            <textarea
              data-testid="report-notes-input"
              style={styles.notesTextarea}
              placeholder="Add resolution notes here…"
              value={notesDraft}
              onChange={(e) => { e.stopPropagation(); setNotesDraft(e.target.value); }}
              onBlur={handleNotesBlur}
            />
          </div>
          <div style={styles.statusSelectRow}>
            <span style={styles.statusSelectLabel}>Status:</span>
            <select
              data-testid="report-status-select"
              style={styles.statusSelect}
              value={status}
              onChange={handleStatusChange}
              onClick={(e) => e.stopPropagation()}
            >
              {REPORT_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          {status !== 'new' && (
            <div
              data-testid="resolved-banner"
              style={styles.resolvedBanner(status === 'fixed')}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.resolvedBannerText}>
                <span style={styles.resolvedBannerHeading(status === 'fixed')}>
                  {status === 'fixed' ? '✓ Fixed' : '— Won\'t Fix'}
                </span>
                <span style={styles.resolvedBannerBody}>
                  {status === 'fixed'
                    ? 'Question corrected and released back into the active pool.'
                    : 'Report closed. Question remains in the active pool.'}
                </span>
              </div>
              {status === 'fixed' && (
                <button
                  data-testid="remove-report-button"
                  style={styles.removeButton}
                  onClick={() => onRemoveReport(reportId)}
                >
                  Remove from list
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── QuestionBankRow sub-component ─────────────────────────────────────────────

function QuestionBankRow({ question, examId, onEditQuestion, onUpdateLifecycle, savingId }) {
  const isSaving = savingId === String(question.id);
  const statusCfg = QUESTION_STATUS_COLORS[question.status]     || QUESTION_STATUS_COLORS.active;
  const reviewCfg = REVIEW_STATUS_COLORS[question.reviewStatus] || REVIEW_STATUS_COLORS.unreviewed;

  return (
    <tr data-testid="qb-row" data-question-id={question.id}>
      <td style={styles.qbTd}>
        <span style={styles.refIdBadge}>{question.referenceId}</span>
      </td>
      <td style={{ ...styles.qbTd, maxWidth: '280px' }}>
        <div style={{
          overflow: 'hidden', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          fontSize: '0.85rem', color: '#333',
        }}>
          {question.question}
        </div>
        {question.category && (
          <span style={{ fontSize: '0.72rem', color: '#888', marginTop: '2px', display: 'block' }}>
            {question.category}
          </span>
        )}
        {question.sourceBatchId && (
          <span style={{ fontSize: '0.7rem', color: '#aaa', fontFamily: 'monospace' }}>
            batch: {question.sourceBatchId}
          </span>
        )}
      </td>
      <td style={styles.qbTd}>
        <span style={styles.lifecycleBadge(statusCfg)}>
          {QUESTION_STATUS_LABELS[question.status] || question.status}
        </span>
      </td>
      <td style={styles.qbTd}>
        <span style={styles.lifecycleBadge(reviewCfg)}>
          {REVIEW_STATUS_LABELS[question.reviewStatus] || question.reviewStatus}
        </span>
      </td>
      <td style={{ ...styles.qbTd, fontSize: '0.78rem', color: '#aaa' }}>
        {formatDateShort(question.updatedAt)}
      </td>
      <td style={styles.qbTd}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {question.status === 'new' && (
            <button
              data-testid="qb-approve-button"
              disabled={isSaving}
              style={styles.actionButton('approve')}
              onClick={() => onUpdateLifecycle(question.id, examId, { status: 'active', reviewStatus: 'reviewed' }, 'Approved via Question Bank')}
            >
              {isSaving ? '…' : '✓ Approve'}
            </button>
          )}
          {(question.reviewStatus === 'unreviewed' || question.reviewStatus === 'reviewed') && (
            <button
              data-testid="qb-report-button"
              disabled={isSaving}
              style={styles.actionButton('report')}
              onClick={() => onUpdateLifecycle(question.id, examId, { reviewStatus: 'reported' }, 'Flagged as reported')}
            >
              Flag
            </button>
          )}
          {question.reviewStatus === 'reported' && (
            <>
              <button
                data-testid="qb-inreview-button"
                disabled={isSaving}
                style={styles.actionButton('fix')}
                onClick={() => onUpdateLifecycle(question.id, examId, { reviewStatus: 'in_review' }, 'Moved to in review')}
              >
                Mark In Review
              </button>
              <button
                data-testid="qb-reviewed-button"
                disabled={isSaving}
                style={styles.actionButton('notFixed')}
                onClick={() => onUpdateLifecycle(question.id, examId, { reviewStatus: 'reviewed' }, 'Marked as reviewed')}
              >
                Mark Reviewed
              </button>
            </>
          )}
          {question.reviewStatus === 'in_review' && (
            <button
              data-testid="qb-reviewed-button"
              disabled={isSaving}
              style={styles.actionButton('fix')}
              onClick={() => onUpdateLifecycle(question.id, examId, { reviewStatus: 'reviewed' }, 'Marked as reviewed')}
            >
              Mark Reviewed
            </button>
          )}
          <button
            data-testid="qb-edit-button"
            disabled={isSaving}
            style={styles.actionButton('edit')}
            onClick={() => onEditQuestion(examId, question.id)}
          >
            Edit
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ReportedQuestionsView({
  reports,
  onUpdateReport,
  onRemoveReport,
  onEditQuestion,
  onBack,
  questionStores = { pmp: null, cpmai: null },
  onUpdateLifecycle,
  initialTab = 'reports',
  initialQbExam = 'all',
  initialQbStatus = 'new',
  initialQbReviewStatus = 'all',
}) {
  // Tab
  const [activeTab, setActiveTab] = useState(initialTab);

  // ── Reports tab state ───────────────────────────────────────────────────────
  const [examFilter,     setExamFilter]     = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter,   setStatusFilter]   = useState('all');
  const [expandedId,     setExpandedId]     = useState(null);

  // ── Question Bank tab state ─────────────────────────────────────────────────
  const [qbExam,         setQbExam]         = useState(initialQbExam);
  const [qbStatus,       setQbStatus]       = useState(initialQbStatus); // default to new
  const [qbReviewStatus, setQbReviewStatus] = useState(initialQbReviewStatus);
  const [qbCategory,     setQbCategory]     = useState('all');
  const [qbSearch,       setQbSearch]       = useState('');
  const [savingId,       setSavingId]       = useState(null); // questionId being saved

  // PR 2C: the app no longer prefetches banks at mount, so the admin Question
  // Bank tab loads them EXPLICITLY here (loadAllCertBanks). The lifted store
  // stays authoritative; loaded banks only fill certs the store hasn't got.
  const [loadedBanks,  setLoadedBanks]  = useState({});
  const [banksLoading, setBanksLoading] = useState(false);
  const [banksError,   setBanksError]   = useState(null);
  useEffect(() => {
    let cancelled = false;
    const missing = getActiveCertIds().filter((id) => !Array.isArray(questionStores[id]));
    if (missing.length === 0) { setBanksLoading(false); return undefined; }
    setBanksLoading(true);
    setBanksError(null);
    loadAllCertBanks()
      .then((banks) => {
        if (cancelled) return;
        const next = {};
        Object.entries(banks).forEach(([id, result]) => { next[id] = result.questions; });
        setLoadedBanks(next);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[ReportedQuestionsView] bank load failed:', err);
        setBanksError(err?.message || 'Failed to load the question bank.');
      })
      .finally(() => { if (!cancelled) setBanksLoading(false); });
    return () => { cancelled = true; };
  }, [questionStores]);

  // ── Reports filter logic ────────────────────────────────────────────────────
  const filteredReports = reports.filter((r) => {
    if (examFilter     !== 'all' && r.examId   !== examFilter)     return false;
    if (categoryFilter !== 'all' && r.category !== categoryFilter) return false;
    if (statusFilter   !== 'all' && r.status   !== statusFilter)   return false;
    return true;
  });

  function toggleExpand(reportId) {
    setExpandedId((prev) => (prev === reportId ? null : reportId));
  }

  const newReportCount = reports.filter((r) => r.status === 'new').length;

  // ── Question Bank: derive unified question list ─────────────────────────────
  const allQuestions = useMemo(() => {
    const list = [];
    getActiveCertIds().forEach((exam) => {
      const store = Array.isArray(questionStores[exam])
        ? questionStores[exam]
        : loadedBanks[exam];
      if (Array.isArray(store)) {
        store.forEach((q) => list.push({ ...q, _examId: exam }));
      }
    });
    return list;
  }, [questionStores, loadedBanks]);

  // All unique categories across both stores (for the filter dropdown)
  const allCategories = useMemo(() => {
    const cats = new Set();
    allQuestions.forEach((q) => { if (q.category) cats.add(q.category); });
    return Array.from(cats).sort();
  }, [allQuestions]);

  const filteredQBQuestions = useMemo(() => {
    return allQuestions.filter((q) => {
      if (qbExam !== 'all' && q._examId !== qbExam) return false;
      if (qbStatus !== 'all' && q.status !== qbStatus) return false;
      if (qbReviewStatus !== 'all' && q.reviewStatus !== qbReviewStatus) return false;
      if (qbCategory === '__uncategorized__' && q.category && q.category.trim()) return false;
      if (qbCategory !== 'all' && qbCategory !== '__uncategorized__' && q.category !== qbCategory) return false;
      if (qbSearch.trim()) {
        const lower = qbSearch.toLowerCase();
        if (
          !q.question.toLowerCase().includes(lower) &&
          !(q.referenceId && q.referenceId.toLowerCase().includes(lower)) &&
          !(q.sourceBatchId && q.sourceBatchId.toLowerCase().includes(lower))
        ) return false;
      }
      return true;
    });
  }, [allQuestions, qbExam, qbStatus, qbReviewStatus, qbCategory, qbSearch]);

  // Count of questions needing attention (status=new OR reviewStatus=reported/in_review)
  const pendingReviewCount = useMemo(() =>
    allQuestions.filter((q) => q.status === 'new' || q.reviewStatus === 'reported' || q.reviewStatus === 'in_review').length,
    [allQuestions]
  );

  // ── Quick-filter chip counts ────────────────────────────────────────────────
  const quickFilterCounts = useMemo(() => ({
    uncategorized: allQuestions.filter((q) => !q.category || !q.category.trim()).length,
    newQuestions:  allQuestions.filter((q) => q.status === 'new').length,
    inReview:      allQuestions.filter((q) => q.reviewStatus === 'in_review').length,
    reported:      allQuestions.filter((q) => q.reviewStatus === 'reported').length,
  }), [allQuestions]);

  // Which quick-filter chip is currently active (null = none)
  const activeQuickFilter = useMemo(() => {
    if (qbCategory === '__uncategorized__' && qbStatus === 'all' && qbReviewStatus === 'all' && qbExam === 'all') return 'uncategorized';
    if (qbStatus === 'new' && qbCategory === 'all' && qbReviewStatus === 'all') return 'newQuestions';
    if (qbReviewStatus === 'in_review' && qbCategory === 'all' && qbStatus === 'all') return 'inReview';
    if (qbReviewStatus === 'reported' && qbCategory === 'all' && qbStatus === 'all') return 'reported';
    return null;
  }, [qbCategory, qbStatus, qbReviewStatus, qbExam]);

  function applyQuickFilter(key) {
    // Toggle off if already active
    if (activeQuickFilter === key) {
      setQbCategory('all');
      setQbStatus('new');
      setQbReviewStatus('all');
      setQbExam('all');
      return;
    }
    // Reset all dropdowns then apply the chosen preset
    setQbExam('all');
    setQbSearch('');
    if (key === 'uncategorized') {
      setQbCategory('__uncategorized__');
      setQbStatus('all');
      setQbReviewStatus('all');
    } else if (key === 'newQuestions') {
      setQbCategory('all');
      setQbStatus('new');
      setQbReviewStatus('all');
    } else if (key === 'inReview') {
      setQbCategory('all');
      setQbStatus('all');
      setQbReviewStatus('in_review');
    } else if (key === 'reported') {
      setQbCategory('all');
      setQbStatus('all');
      setQbReviewStatus('reported');
    }
  }

  // Lifecycle update wrapper — sets savingId, calls prop, clears
  async function handleUpdateLifecycle(questionId, examId, changes, changeNote) {
    if (!onUpdateLifecycle) return;
    setSavingId(String(questionId));
    try {
      await onUpdateLifecycle(questionId, examId, changes, changeNote);
    } finally {
      setSavingId(null);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div data-testid="reported-questions-view" style={styles.wrapper}>

      {/* Header */}
      <div style={styles.header}>
        <button
          data-testid="reported-questions-back-button"
          style={styles.backButton}
          onClick={onBack}
        >
          ← Back
        </button>
        <span style={styles.headerTitle}>⚑ Question Review</span>
        <span style={styles.headerCount}>
          {reports.length} report{reports.length !== 1 ? 's' : ''} · {newReportCount} new
          {pendingReviewCount > 0 && ` · ${pendingReviewCount} awaiting review`}
        </span>
      </div>

      {/* Tab bar */}
      <div style={styles.tabBar}>
        <span
          data-testid="tab-reports"
          style={styles.tab(activeTab === 'reports')}
          onClick={() => setActiveTab('reports')}
        >
          Reports {newReportCount > 0 && `(${newReportCount} new)`}
        </span>
        <span
          data-testid="tab-question-bank"
          style={styles.tab(activeTab === 'question-bank')}
          onClick={() => setActiveTab('question-bank')}
        >
          Question Bank {pendingReviewCount > 0 && `(${pendingReviewCount} pending)`}
        </span>
      </div>

      <div style={styles.body}>

        {/* ── Reports tab ───────────────────────────────────────────────────── */}
        {activeTab === 'reports' && (
          <>
            <div style={styles.filterBar}>
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Exam</span>
                <select data-testid="filter-exam" style={styles.filterSelect}
                  value={examFilter} onChange={(e) => setExamFilter(e.target.value)}>
                  {/* Driven from getAllExams() so new certifications added in
                      src/config/exams.js show up here automatically. */}
                  <option value="all">All exams</option>
                  {getAllExams().map((exam) => (
                    <option key={exam.examId} value={exam.examId}>
                      {exam.displayName}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Category</span>
                <select data-testid="filter-category" style={styles.filterSelect}
                  value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                  <option value="all">All categories</option>
                  {REPORT_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Status</span>
                <select data-testid="filter-status" style={styles.filterSelect}
                  value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All statuses</option>
                  {REPORT_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {filteredReports.length === 0 ? (
              <p style={styles.emptyState}>
                {reports.length === 0
                  ? 'No reports have been filed yet.'
                  : 'No reports match the current filters.'}
              </p>
            ) : (
              <div style={styles.reportList}>
                {filteredReports.map((report) => (
                  <ReportCard
                    key={report.reportId}
                    report={report}
                    isExpanded={expandedId === report.reportId}
                    onToggle={() => toggleExpand(report.reportId)}
                    onUpdateReport={onUpdateReport}
                    onRemoveReport={onRemoveReport}
                    onEditQuestion={onEditQuestion}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Question Bank tab ─────────────────────────────────────────────── */}
        {activeTab === 'question-bank' && (
          <>
            {/* Filter bar */}
            <div style={styles.filterBar}>
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Exam</span>
                <select data-testid="qb-filter-exam" style={styles.filterSelect}
                  value={qbExam} onChange={(e) => setQbExam(e.target.value)}>
                  {/* Driven from getAllExams() so new certifications added in
                      src/config/exams.js show up here automatically. */}
                  <option value="all">All exams</option>
                  {getAllExams().map((exam) => (
                    <option key={exam.examId} value={exam.examId}>
                      {exam.displayName}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Question Status</span>
                <select data-testid="qb-filter-status" style={styles.filterSelect}
                  value={qbStatus} onChange={(e) => setQbStatus(e.target.value)}>
                  <option value="all">All statuses</option>
                  {QUESTION_STATUSES.map((s) => (
                    <option key={s} value={s}>{QUESTION_STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Review Status</span>
                <select data-testid="qb-filter-review" style={styles.filterSelect}
                  value={qbReviewStatus} onChange={(e) => setQbReviewStatus(e.target.value)}>
                  <option value="all">All review statuses</option>
                  {REVIEW_STATUSES.map((s) => (
                    <option key={s} value={s}>{REVIEW_STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Category</span>
                <select data-testid="qb-filter-category" style={styles.filterSelect}
                  value={qbCategory} onChange={(e) => setQbCategory(e.target.value)}>
                  <option value="all">All categories</option>
                  {allCategories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div style={{ ...styles.filterGroup, flex: 2 }}>
                <span style={styles.filterLabel}>Search</span>
                <input
                  data-testid="qb-search"
                  type="text"
                  placeholder="Question text, reference ID, or batch ID…"
                  value={qbSearch}
                  onChange={(e) => setQbSearch(e.target.value)}
                  style={{
                    padding: '7px 10px', fontSize: '0.9rem',
                    border: '1px solid #ccc', borderRadius: '6px',
                    background: '#fff', color: '#333',
                  }}
                />
              </div>
            </div>

            {/* Quick-filter chips */}
            {allQuestions.length > 0 && (
              <div style={styles.quickFilterRow}>
                {[
                  { key: 'uncategorized', label: 'Not Categorized', count: quickFilterCounts.uncategorized },
                  { key: 'newQuestions',  label: 'New Questions',   count: quickFilterCounts.newQuestions },
                  { key: 'reported',      label: 'Reported',        count: quickFilterCounts.reported },
                  { key: 'inReview',      label: 'In Review',       count: quickFilterCounts.inReview },
                ].map(({ key, label, count }) => {
                  const active = activeQuickFilter === key;
                  return (
                    <button
                      key={key}
                      onClick={() => applyQuickFilter(key)}
                      style={styles.quickFilterChip(active)}
                      title={active ? 'Click to clear filter' : `Show ${label.toLowerCase()}`}
                    >
                      {label}
                      <span style={styles.quickFilterBadge(active)}>{count}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Summary line */}
            <div style={{ fontSize: '0.82rem', color: '#888', marginBottom: '12px' }}>
              {filteredQBQuestions.length} question{filteredQBQuestions.length !== 1 ? 's' : ''} shown
              {allQuestions.length !== filteredQBQuestions.length && ` of ${allQuestions.length} loaded`}
            </div>

            {/* Table */}
            {filteredQBQuestions.length === 0 ? (
              <p style={styles.emptyState} data-testid="qb-empty-state">
                {banksLoading
                  ? 'Loading the question bank…'
                  : banksError
                    ? `Could not load the question bank: ${banksError}`
                    : allQuestions.length === 0
                      ? 'No questions loaded.'
                      : 'No questions match the current filters.'}
              </p>
            ) : (
              <table style={styles.qbTable} data-testid="qb-table">
                <thead>
                  <tr>
                    <th style={{ ...styles.qbTh, width: '100px' }}>Ref ID</th>
                    <th style={styles.qbTh}>Question</th>
                    <th style={{ ...styles.qbTh, width: '90px' }}>Status</th>
                    <th style={{ ...styles.qbTh, width: '110px' }}>Review</th>
                    <th style={{ ...styles.qbTh, width: '90px' }}>Updated</th>
                    <th style={{ ...styles.qbTh, width: '220px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQBQuestions.map((q) => (
                    <QuestionBankRow
                      key={`${q._examId}-${q.id}`}
                      question={q}
                      examId={q._examId}
                      onEditQuestion={onEditQuestion}
                      onUpdateLifecycle={handleUpdateLifecycle}
                      savingId={savingId}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

      </div>
    </div>
  );
}
